import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SpinningIco } from "@/components/brand/SpinningIco";
import { Wordmark } from "@/components/brand/Wordmark";
import { supabase } from "@/lib/supabase";

/**
 * /forgot-password - request a password-reset email.
 *
 * Why this page exists even though the mobile app has its own
 * forgot-password screen: Supabase's PKCE flow stores a code_verifier
 * client-side, and only the client that *sent* the reset request can
 * exchange the resulting code. When the mobile app initiates the reset,
 * the email link can only be consumed by the mobile app - and custom
 * URL schemes (whistll://) are increasingly blocked by email clients.
 *
 * So we offer a web entry point. The user requests the reset here, the
 * email link redirects to /auth/reset-password on this same origin, and
 * the verifier is in the browser's localStorage where the SDK expects
 * to find it. Same-client end-to-end, no deep-link plumbing needed.
 *
 * Mobile users with broken reset emails are pointed here as a fallback.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("That doesn't look like a valid email address.");
      return;
    }

    setSubmitting(true);
    const { error: authError } = await supabase.auth.resetPasswordForEmail(
      trimmed,
      {
        // Same-origin redirect - guarantees the email link lands back on
        // *this* client, where the PKCE code_verifier was stored.
        redirectTo: `${window.location.origin}/auth/reset-password`,
      },
    );
    setSubmitting(false);

    if (authError) {
      setError(authError.message);
      return;
    }
    setSent(true);
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-12">
      {/* Volt glow backdrop - matches /login for visual continuity. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[640px] opacity-30 blur-3xl"
        style={{
          backgroundImage:
            "radial-gradient(closest-side at 50% 0%, hsl(var(--volt)), transparent 70%)",
        }}
      />

      <div className="w-full max-w-md">
        <div className="mb-10 flex flex-col items-center gap-3">
          <SpinningIco size={120} />
          <Wordmark size="text-3xl" />
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl sm:p-8">
          {!sent ? (
            <>
              <div className="mb-5">
                <h1 className="text-2xl font-semibold tracking-tight">
                  Reset your password
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter the email on your account and we'll send a recovery
                  link. The link expires in 60 minutes.
                </p>
              </div>

              <form onSubmit={onSubmit} className="flex flex-col gap-4">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium">Email</span>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </label>

                {error && (
                  <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button type="submit" size="lg" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" aria-hidden />
                      Send recovery link
                    </>
                  )}
                </Button>
              </form>

              <p className="mt-5 text-center text-xs text-muted-foreground">
                Remembered it?{" "}
                <Link
                  to="/login"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Back to sign in
                </Link>
              </p>
            </>
          ) : (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-volt text-primary-foreground">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-semibold">Check your email</h2>
                <p className="text-sm text-muted-foreground">
                  If an account exists for{" "}
                  <span className="font-semibold text-foreground">
                    {email}
                  </span>
                  , we just sent a recovery link. Open it on this device - the
                  reset has to happen in the same browser you requested it
                  from.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setEmail("");
                }}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                Wrong email? Try again
              </button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Reset link not arriving? Email{" "}
          <a
            href="mailto:hello@whistll.com?subject=Password%20reset"
            className="underline underline-offset-2 hover:text-foreground"
          >
            hello@whistll.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}
